import React, { useState, useEffect, useContext } from "react";
import { OrgContext } from "contexts/OrgContext";
import { MoreHorizontal, Pencil, Plus } from "lucide-react";
import { PortProtocol } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/network_pb";
import type { DisplayPort } from "utils/environment/networkAccessFallback";
import { Button } from "components/Graphs/Button";
import { Input } from "components/Graphs/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import {
  RadioCardGroup,
  RadioCardIndicator,
  RadioCardItem,
} from "components/Graphs/RadioCardGroup";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/Graphs/Dialogue";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/Graphs/Dropdown";
import {
  NodeDetailIconAction,
  NodeDetailSectionDescription,
  NodeDetailSectionHeader,
  NodeDetailTable,
  NodeDetailTableBody,
  NodeDetailTableCell,
  NodeDetailTableHead,
  NodeDetailTableHeaderCell,
} from "components/NodeDetail/shared/NodeDetailSection";
import {
  AllowedSourcesEditor,
  AllowedSourcesValue,
  CopyButton,
  ConfirmDialog,
  NetworkTableLoadingRow,
  ScrollableNetworkDialogBody,
  ScrollableNetworkDialogContent,
  type PortType,
} from "./shared";
import { isOrgAdminOrMaintainerRole } from "utils/roleUtils";

// ─── helpers ─────────────────────────────────────────────────────────────────

const MAX_ALLOWED_SOURCES_IN_ROW = 6;
const MAX_PORT_RANGE_SIZE = 32;

function stringArrayEquals(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

function protocolLabel(port: DisplayPort): string {
  const protocol = port.protocol;
  switch (protocol) {
    case PortProtocol.SSH:
      return "SSH";
    case PortProtocol.TCP:
      return "TCP";
    case PortProtocol.UDP:
      return "UDP";
    default:
      return "Unknown";
  }
}

function publicPortLabel(port: DisplayPort): string {
  if (port.isEndpointPending) return "pending";
  return String(port.portNumber);
}

function formatEndpoint(port: DisplayPort): string | null {
  if (port.isEndpointPending) return "pending";
  if (!port.hostname) return null;
  return `${port.hostname}:${publicPortLabel(port)}`;
}

function destinationPortLabel(port: DisplayPort): string | null {
  return port.serverPort ? String(port.serverPort) : null;
}

function effectiveServerPort(port: DisplayPort): number {
  return port.serverPort || port.portNumber;
}

function comparePortsByServerPort(
  firstPort: DisplayPort,
  secondPort: DisplayPort,
): number {
  return (
    effectiveServerPort(firstPort) - effectiveServerPort(secondPort) ||
    firstPort.portNumber - secondPort.portNumber ||
    firstPort.portId.localeCompare(secondPort.portId)
  );
}

function shouldShowAllowedSources(port: DisplayPort): boolean {
  return port.hideAllowedSources !== true;
}

function PortAllowedSourcesValue({
  port,
  onMoreClick,
}: {
  port: DisplayPort;
  onMoreClick?: () => void;
}) {
  if (!shouldShowAllowedSources(port)) {
    return <span className="text-gray-400 dark:text-zinc-600">—</span>;
  }

  return (
    <AllowedSourcesValue
      allowedSources={port.allowedSources}
      maxVisible={MAX_ALLOWED_SOURCES_IN_ROW}
      onMoreClick={onMoreClick}
    />
  );
}

function EndpointValue({ port }: { port: DisplayPort }) {
  const endpoint = formatEndpoint(port);
  const isPending = port.isEndpointPending;

  if (!endpoint) {
    return <span className="text-gray-400 dark:text-zinc-600">—</span>;
  }

  return (
    <span
      className={`flex items-center gap-1 font-mono ${
        isPending ? "italic text-gray-400 dark:text-zinc-500" : ""
      }`}
    >
      {endpoint}
      {!isPending && <CopyButton value={endpoint} />}
    </span>
  );
}

function isPublicPortPending(port: DisplayPort): boolean {
  return port.isEndpointPending === true;
}

// ─── open port dialog ─────────────────────────────────────────────────────────

export interface OpenPortInitialValues {
  portType: PortType;
  portNumber: string;
  allowedSources: string[];
  isEditingTarget?: boolean;
}

export interface OpenPortDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    portType: PortType,
    portNumber: number,
    allowedSources: string[],
    toPortNumber?: number,
  ) => Promise<void>;
  existingPorts: DisplayPort[];
  initialValues?: OpenPortInitialValues;
  defaultPortType?: PortType;
}

export const OpenPortDialog: React.FC<OpenPortDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  existingPorts,
  initialValues,
  defaultPortType = "TCP",
}) => {
  const [portType, setPortType] = useState<PortType>(defaultPortType);
  const [portSelection, setPortSelection] = useState<"single" | "range">(
    "single",
  );
  const [portNumber, setPortNumber] = useState("");
  const [toPortNumber, setToPortNumber] = useState("");
  const [allowedSources, setAllowedSources] = useState<string[]>([]);
  const [sourceInput, setSourceInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = initialValues !== undefined;
  const isEditingTarget = initialValues?.isEditingTarget === true;

  useEffect(() => {
    if (!open) return;
    if (initialValues) {
      setPortType(initialValues.portType);
      setPortSelection("single");
      setPortNumber(initialValues.portNumber);
      setToPortNumber("");
      setAllowedSources(initialValues.allowedSources);
      setSourceInput("");
    } else {
      setPortType(defaultPortType);
      setPortSelection("single");
      setPortNumber("");
      setToPortNumber("");
      setAllowedSources([]);
      setSourceInput("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const portNum = parseInt(portNumber, 10);
  const toPortNum = parseInt(toPortNumber, 10);
  const isRange = portSelection === "range";
  const isValidPortNumber = !isNaN(portNum) && portNum > 0 && portNum <= 65535;
  const isValidToPortNumber =
    !isNaN(toPortNum) && toPortNum > 0 && toPortNum <= 65535;
  const portRangeSize = toPortNum - portNum + 1;
  const exceedsMaxPortRange =
    isRange &&
    isValidPortNumber &&
    isValidToPortNumber &&
    portRangeSize > MAX_PORT_RANGE_SIZE;
  const isValidPortRange =
    !isRange ||
    (isValidToPortNumber && portNum < toPortNum && !exceedsMaxPortRange);
  const alreadyAllocated =
    !isEditingTarget &&
    isValidPortNumber &&
    isValidPortRange &&
    existingPorts.some((port) => {
      const existingPortNumber = port.serverPort || port.portNumber;
      return isRange
        ? existingPortNumber >= portNum && existingPortNumber <= toPortNum
        : port.portNumber === portNum;
    });
  const alreadyHasSsh =
    portType === "SSH" &&
    existingPorts.some((p) => p.protocol === PortProtocol.SSH);
  const isValid =
    isValidPortNumber &&
    isValidPortRange &&
    !alreadyAllocated &&
    !alreadyHasSsh;

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleAddSource = () => {
    const trimmed = sourceInput.trim();
    if (!trimmed || allowedSources.includes(trimmed)) return;
    setAllowedSources((prev) => [...prev, trimmed]);
    setSourceInput("");
  };

  const handleRemoveSource = (source: string) => {
    setAllowedSources((prev) => prev.filter((item) => item !== source));
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      await onSubmit(
        portType,
        portNum,
        allowedSources,
        isRange ? toPortNum : undefined,
      );
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <ScrollableNetworkDialogContent>
        <DialogHeader className="pb-2">
          <DialogTitle className="text-white">
            {isEditing ? "Edit Port" : "Open Port"}
          </DialogTitle>
          <DialogDescription className="text-neutral-400">
            {isEditing
              ? "Update the configuration for this port."
              : "Expose a port on this node to allow inbound traffic."}
          </DialogDescription>
        </DialogHeader>

        <ScrollableNetworkDialogBody>
          {!isEditing && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm text-neutral-400">Ports</label>
              <RadioCardGroup
                aria-label="Port selection"
                value={portSelection}
                onValueChange={(value) => {
                  setPortSelection(value as "single" | "range");
                }}
                className="grid grid-cols-2"
              >
                <RadioCardItem
                  value="single"
                  className="flex cursor-pointer items-center gap-2 bg-secondaryBg p-3"
                >
                  <RadioCardIndicator />
                  <span className="text-sm text-primary">Single port</span>
                </RadioCardItem>
                <RadioCardItem
                  value="range"
                  className="flex cursor-pointer items-center gap-2 bg-secondaryBg p-3"
                >
                  <RadioCardIndicator />
                  <span className="text-sm text-primary">Port range</span>
                </RadioCardItem>
              </RadioCardGroup>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-neutral-400">Protocol</label>
            {isEditing ? (
              <p className="text-sm text-white">{portType}</p>
            ) : (
              <Select
                value={portType}
                onValueChange={(v) => setPortType(v as PortType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TCP">TCP</SelectItem>
                  <SelectItem value="UDP">UDP</SelectItem>
                </SelectContent>
              </Select>
            )}
            {alreadyHasSsh && (
              <p className="text-xs text-red-400">
                An SSH port is already exposed on this node. Only one SSH port
                is allowed.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            {isRange ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="open-port-range-from"
                    className="text-sm text-neutral-400"
                  >
                    From port
                  </label>
                  <Input
                    id="open-port-range-from"
                    type="number"
                    placeholder="8000"
                    value={portNumber}
                    onChange={(event) => setPortNumber(event.target.value)}
                    min={1}
                    max={65535}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="open-port-range-to"
                    className="text-sm text-neutral-400"
                  >
                    To port
                  </label>
                  <Input
                    id="open-port-range-to"
                    type="number"
                    placeholder="8003"
                    value={toPortNumber}
                    onChange={(event) => setToPortNumber(event.target.value)}
                    min={1}
                    max={65535}
                  />
                </div>
              </div>
            ) : (
              <>
                <label className="text-sm text-neutral-400">
                  {isEditingTarget ? "Destination port" : "Port number"}
                </label>
                <Input
                  type="number"
                  placeholder="8080"
                  value={portNumber}
                  onChange={(event) => setPortNumber(event.target.value)}
                  min={1}
                  max={65535}
                />
              </>
            )}
            {isRange && isValidPortNumber && portNum >= toPortNum && (
              <p className="text-xs text-red-400">
                To port must be greater than from port.
              </p>
            )}
            {exceedsMaxPortRange && (
              <p className="text-xs text-red-400">
                A port range can include at most {MAX_PORT_RANGE_SIZE} ports.
              </p>
            )}
            {alreadyAllocated && (
              <p className="text-xs text-red-400">
                {isRange
                  ? "This range overlaps a port already open on this node."
                  : `Port ${portNum} is already open on this node.`}
              </p>
            )}
            {isEditingTarget && (
              <p className="text-xs text-neutral-500">
                Update the internal server port this public port forwards to.
              </p>
            )}
          </div>

          <AllowedSourcesEditor
            allowedSources={allowedSources}
            sourceInput={sourceInput}
            onSourceInputChange={setSourceInput}
            onAddSource={handleAddSource}
            onRemoveSource={handleRemoveSource}
            helperText="Use 0.0.0.0/0 to allow all. Remove all entries to clear the list."
          />
        </ScrollableNetworkDialogBody>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!isValid}
            isLoading={isSubmitting}
            className="disabled:bg-highlight disabled:text-black"
            loadingText={
              isEditing
                ? "Saving..."
                : isRange
                  ? "Opening Ports..."
                  : "Opening Port..."
            }
          >
            {isEditing ? "Save Changes" : isRange ? "Open Ports" : "Open Port"}
          </Button>
        </DialogFooter>
      </ScrollableNetworkDialogContent>
    </Dialog>
  );
};

const EditDestinationPortDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  port: DisplayPort;
  onSubmit: (portNumber: number) => Promise<void>;
}> = ({ open, onOpenChange, port, onSubmit }) => {
  const [portNumber, setPortNumber] = useState(
    String(port.serverPort || port.portNumber),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPortNumber(String(port.serverPort || port.portNumber));
  }, [open, port.portNumber, port.serverPort]);

  const parsedPortNumber = parseInt(portNumber, 10);
  const isValid =
    !isNaN(parsedPortNumber) &&
    parsedPortNumber > 0 &&
    parsedPortNumber <= 65535;

  const handleSubmit = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      await onSubmit(parsedPortNumber);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onOpenChange(false);
      }}
    >
      <ScrollableNetworkDialogContent>
        <DialogHeader className="pb-2">
          <DialogTitle className="text-white">
            Edit Destination Port
          </DialogTitle>
          <DialogDescription className="text-neutral-400">
            Update the internal server port this public port forwards to.
          </DialogDescription>
        </DialogHeader>
        <ScrollableNetworkDialogBody>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-neutral-400">Destination port</label>
            <Input
              type="number"
              placeholder="8080"
              value={portNumber}
              onChange={(event) => setPortNumber(event.target.value)}
              min={1}
              max={65535}
            />
          </div>
        </ScrollableNetworkDialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!isValid}
            isLoading={isSubmitting}
            loadingText="Saving..."
          >
            Save Changes
          </Button>
        </DialogFooter>
      </ScrollableNetworkDialogContent>
    </Dialog>
  );
};

const EditIPRestrictionsDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  port: DisplayPort;
  onSubmit: (allowedSources: string[]) => Promise<void>;
}> = ({ open, onOpenChange, port, onSubmit }) => {
  const [allowedSources, setAllowedSources] = useState<string[]>(
    port.allowedSources || [],
  );
  const [sourceInput, setSourceInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAllowedSources(port.allowedSources || []);
    setSourceInput("");
  }, [open, port.allowedSources]);

  const handleAddSource = () => {
    const trimmed = sourceInput.trim();
    if (!trimmed || allowedSources.includes(trimmed)) return;
    setAllowedSources((previous) => [...previous, trimmed]);
    setSourceInput("");
  };

  const handleRemoveSource = (source: string) => {
    setAllowedSources((previous) => previous.filter((item) => item !== source));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(allowedSources);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onOpenChange(false);
      }}
    >
      <ScrollableNetworkDialogContent>
        <DialogHeader className="pb-2">
          <DialogTitle className="text-white">Edit IP Restrictions</DialogTitle>
          <DialogDescription className="text-neutral-400">
            Control which source IP addresses can reach this port.
          </DialogDescription>
        </DialogHeader>
        <ScrollableNetworkDialogBody>
          <AllowedSourcesEditor
            allowedSources={allowedSources}
            sourceInput={sourceInput}
            onSourceInputChange={setSourceInput}
            onAddSource={handleAddSource}
            onRemoveSource={handleRemoveSource}
          />
        </ScrollableNetworkDialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Saving..."
          >
            Save Changes
          </Button>
        </DialogFooter>
      </ScrollableNetworkDialogContent>
    </Dialog>
  );
};

export interface NodePortsSectionProps {
  ports: DisplayPort[];
  isLoading?: boolean;
  canOpenPort?: boolean;
  canManagePort?: (port: DisplayPort) => boolean;
  onOpenPort: (
    portNumber: number,
    protocol: PortProtocol,
    allowedSources: string[],
    toPortNumber?: number,
  ) => Promise<void>;
  onSetPortTarget: (portId: string, portNumber: number) => Promise<void>;
  onSetPortAllowedSources: (
    portId: string,
    allowedSources: string[],
  ) => Promise<void>;
  onClosePort: (portId: string) => Promise<void>;
}

export const NodePortsSection: React.FC<NodePortsSectionProps> = ({
  ports,
  isLoading = false,
  canOpenPort = true,
  canManagePort = () => true,
  onOpenPort,
  onSetPortTarget,
  onSetPortAllowedSources,
  onClosePort,
}) => {
  const { activeOrgRole } = useContext(OrgContext);
  const isAdminOrMaintainer = isOrgAdminOrMaintainerRole(activeOrgRole);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDestinationPort, setEditingDestinationPort] =
    useState<DisplayPort | null>(null);
  const [editingIPRestrictionsPort, setEditingIPRestrictionsPort] =
    useState<DisplayPort | null>(null);
  const [portToClose, setPortToClose] = useState<DisplayPort | null>(null);
  const columnCount = isAdminOrMaintainer ? 6 : 5;
  const orderedPorts = [...ports].sort(comparePortsByServerPort);

  const handleSubmit = async (
    portType: PortType,
    portNumber: number,
    allowedSources: string[],
    toPortNumber?: number,
  ) => {
    let protocol: PortProtocol;
    switch (portType) {
      case "UDP":
        protocol = PortProtocol.UDP;
        break;
      case "SSH":
        protocol = PortProtocol.SSH;
        break;
      default:
        protocol = PortProtocol.TCP;
        break;
    }
    await onOpenPort(portNumber, protocol, allowedSources, toPortNumber);
  };

  const handleEditDestinationPort = async (portNumber: number) => {
    const currentPort = editingDestinationPort!;
    if ((currentPort.serverPort || currentPort.portNumber) === portNumber)
      return;

    await onSetPortTarget(currentPort.portId, portNumber);
  };

  const handleEditIPRestrictions = async (allowedSources: string[]) => {
    const currentPort = editingIPRestrictionsPort!;
    if (stringArrayEquals(currentPort.allowedSources ?? [], allowedSources)) {
      return;
    }

    await onSetPortAllowedSources(currentPort.portId, allowedSources);
  };

  return (
    <>
      <NodeDetailSectionHeader
        trailing={
          isAdminOrMaintainer ? (
            <Button
              variant="primary"
              onClick={() => {
                if (canOpenPort) setDialogOpen(true);
              }}
              className="flex items-center gap-1.5"
              disabled={!canOpenPort}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              TCP/UDP port
            </Button>
          ) : null
        }
      >
        <NodeDetailSectionDescription>
          Expose raw TCP or UDP ports for direct access from allowed sources.
        </NodeDetailSectionDescription>
      </NodeDetailSectionHeader>

      <NodeDetailTable>
        <NodeDetailTableHead>
          <tr>
            <NodeDetailTableHeaderCell className="w-full">
              Endpoint
            </NodeDetailTableHeaderCell>
            <NodeDetailTableHeaderCell className="min-w-48">
              IP Restrictions
            </NodeDetailTableHeaderCell>
            <NodeDetailTableHeaderCell>Public Port</NodeDetailTableHeaderCell>
            <NodeDetailTableHeaderCell>
              Destination Port
            </NodeDetailTableHeaderCell>
            <NodeDetailTableHeaderCell>Service Type</NodeDetailTableHeaderCell>
            {isAdminOrMaintainer && (
              <NodeDetailTableHeaderCell>
                <span className="sr-only">Actions</span>
              </NodeDetailTableHeaderCell>
            )}
          </tr>
        </NodeDetailTableHead>
        <NodeDetailTableBody>
          {isLoading ? (
            <NetworkTableLoadingRow
              colSpan={columnCount}
              label="Loading ports..."
            />
          ) : ports.length === 0 ? (
            <tr>
              <NodeDetailTableCell
                colSpan={columnCount}
                className="py-6 text-center text-gray-500 dark:text-secondary"
              >
                No TCP/UDP ports are open on this node.
              </NodeDetailTableCell>
            </tr>
          ) : (
            orderedPorts.map((port) => {
              const canEditPort = isAdminOrMaintainer && canManagePort(port);

              return (
                <tr key={port.portId} className="group">
                  <NodeDetailTableCell className="w-full text-gray-500 dark:text-secondary">
                    <EndpointValue port={port} />
                  </NodeDetailTableCell>
                  <NodeDetailTableCell className="min-w-48">
                    <div className="flex items-start gap-1">
                      <PortAllowedSourcesValue
                        port={port}
                        onMoreClick={
                          canEditPort && shouldShowAllowedSources(port)
                            ? () => setEditingIPRestrictionsPort(port)
                            : undefined
                        }
                      />
                      {canEditPort && shouldShowAllowedSources(port) && (
                        <NodeDetailIconAction
                          className="shrink-0"
                          onClick={() => setEditingIPRestrictionsPort(port)}
                          aria-label="Edit IP restrictions"
                          title="Edit IP restrictions"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </NodeDetailIconAction>
                      )}
                    </div>
                  </NodeDetailTableCell>
                  <NodeDetailTableCell
                    className={`font-mono ${
                      isPublicPortPending(port)
                        ? "italic text-gray-400 dark:text-zinc-500"
                        : "text-gray-900 dark:text-white"
                    }`}
                  >
                    {publicPortLabel(port)}
                  </NodeDetailTableCell>
                  <NodeDetailTableCell className="font-mono text-gray-500 dark:text-secondary">
                    <span className="inline-flex items-center gap-1">
                      {destinationPortLabel(port) ? (
                        <span>{destinationPortLabel(port)}</span>
                      ) : (
                        <span className="text-gray-400 dark:text-zinc-600">
                          —
                        </span>
                      )}
                      {canEditPort && (
                        <NodeDetailIconAction
                          onClick={() => setEditingDestinationPort(port)}
                          aria-label="Edit destination port"
                          title="Edit destination port"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </NodeDetailIconAction>
                      )}
                    </span>
                  </NodeDetailTableCell>
                  <NodeDetailTableCell className="font-mono text-gray-500 dark:text-secondary">
                    {protocolLabel(port)}
                  </NodeDetailTableCell>
                  {isAdminOrMaintainer && (
                    <NodeDetailTableCell align="right">
                      {canEditPort && (
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <NodeDetailIconAction aria-label="More port actions">
                              <MoreHorizontal className="h-5 w-5" />
                            </NodeDetailIconAction>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent side="bottom" align="end">
                            <DropdownMenuItem
                              className="text-red-400 focus:text-red-400"
                              onClick={() => setPortToClose(port)}
                            >
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </NodeDetailTableCell>
                  )}
                </tr>
              );
            })
          )}
        </NodeDetailTableBody>
      </NodeDetailTable>

      <OpenPortDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDialogOpen(false);
          }
        }}
        onSubmit={handleSubmit}
        existingPorts={ports}
        defaultPortType="TCP"
      />
      {editingDestinationPort && (
        <EditDestinationPortDialog
          open={editingDestinationPort !== null}
          onOpenChange={(open) => {
            if (!open) setEditingDestinationPort(null);
          }}
          port={editingDestinationPort}
          onSubmit={handleEditDestinationPort}
        />
      )}
      {editingIPRestrictionsPort && (
        <EditIPRestrictionsDialog
          open={editingIPRestrictionsPort !== null}
          onOpenChange={(open) => {
            if (!open) setEditingIPRestrictionsPort(null);
          }}
          port={editingIPRestrictionsPort}
          onSubmit={handleEditIPRestrictions}
        />
      )}
      <ConfirmDialog
        open={portToClose !== null}
        onOpenChange={(open) => {
          if (!open) setPortToClose(null);
        }}
        title="Remove Port"
        description="This public port will be permanently removed from this node. Active connections may be dropped and this action cannot be undone."
        confirmLabel="Remove Port"
        confirmLoadingLabel="Removing Port..."
        details={[
          {
            label: "Endpoint",
            value: portToClose ? (formatEndpoint(portToClose) ?? "—") : "—",
          },
          {
            label: "Public Port",
            value: portToClose ? publicPortLabel(portToClose) : "—",
          },
          {
            label: "Destination Port",
            value: portToClose
              ? (destinationPortLabel(portToClose) ?? "—")
              : "—",
          },
          {
            label: "Service Type",
            value: portToClose ? protocolLabel(portToClose) : "—",
          },
        ]}
        onConfirm={() => onClosePort(portToClose!.portId)}
      />
    </>
  );
};
